import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApplyDefaultsSheet } from "./ApplyDefaultsSheet";
import { UpdateGeniesSheet } from "./UpdateGeniesSheet";
import { VaultDrawer } from "./VaultDrawer";
import { findChangedPronunciations, removedPronunciations } from "@/lib/pronunciations";
import {
  useCreatePronunciationVault,
  useDeletePronunciationVault,
  usePronunciationVaultList,
  useUpdatePronunciationVault,
} from "@/hooks/agent/usePronunciationVaults";
import { PronunciationChange, PronunciationVault, PronunciationVaultInput } from "@/types/agent/pronunciation-vault";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const emptyInput = (): PronunciationVaultInput => ({
  name: "",
  description: "",
  accent: "",
  pairs: [],
  is_public: true,
  is_default: false,
});

const PronunciationVaultsPage = () => {
  const { data: vaults = [], isLoading } = usePronunciationVaultList();
  const createVault = useCreatePronunciationVault();
  const updateVault = useUpdatePronunciationVault();
  const deleteVault = useDeletePronunciationVault();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [applySheetOpen, setApplySheetOpen] = useState(false);
  const [editingVault, setEditingVault] = useState<PronunciationVault | null>(null);
  const [form, setForm] = useState<PronunciationVaultInput>(emptyInput());
  const [pronunciationChanges, setPronunciationChanges] = useState<PronunciationChange[]>([]);

  const openCreate = () => {
    setEditingVault(null);
    setForm(emptyInput());
    setSheetOpen(true);
  };

  const openEdit = (vault: PronunciationVault) => {
    setEditingVault(vault);
    setForm({
      name: vault.name,
      description: vault.description ?? "",
      accent: vault.accent ?? "",
      pairs: vault.pairs,
      is_public: vault.is_public,
      is_default: vault.is_default,
    });
    setSheetOpen(true);
  };

  const handleDelete = async (vault: PronunciationVault) => {
    deleteVault.mutate(vault.id, {
      onSuccess: () => {
        toast.success(`"${vault.name}" deleted`);
        if (vault.is_default) setPronunciationChanges(removedPronunciations(vault.pairs));
      },
      onError: () => toast.error("Failed to delete vault"),
    });
  };

  const handleSave = () => {
    const input: PronunciationVaultInput = {
      ...form,
      accent: form.accent || undefined,
      description: form.description || undefined,
    };

    if (!input.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (editingVault) {
      const changes = !editingVault.is_default
        ? []
        : input.is_default
          ? findChangedPronunciations(editingVault.pairs, input.pairs)
          : removedPronunciations(editingVault.pairs);
      updateVault.mutate(
        { id: editingVault.id, input },
        {
          onSuccess: () => {
            toast.success("Vault updated");
            setSheetOpen(false);
            setPronunciationChanges(changes);
          },
          onError: () => toast.error("Failed to update vault"),
        },
      );
    } else {
      createVault.mutate(input, {
        onSuccess: () => {
          toast.success("Vault created");
          setSheetOpen(false);
        },
        onError: () => toast.error("Failed to create vault"),
      });
    }
  };

  const isSaving = createVault.isPending || updateVault.isPending;

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <GenieIcon name="microphone" size={32} className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold text-genie-text-primary">Pronunciation Vaults</h1>
            <p className="text-genie-text-secondary">Curated pronunciation collections users can apply to their genies</p>
          </div>
          <div className="ml-auto flex flex-wrap justify-end gap-2 md:pr-[140px]">
            {vaults.some((vault) => vault.is_default) && (
              <Button variant="outline" onClick={() => setApplySheetOpen(true)}>
                Apply defaults to all genies
              </Button>
            )}
            <Button onClick={openCreate} className="gap-1.5 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0">
              <Plus className="w-4 h-4" />
              New Vault
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-genie-border">
          {isLoading ? (
            <div className="text-sm text-genie-text-secondary py-12 text-center">Loading...</div>
          ) : vaults.length === 0 ? (
            <div className="text-center py-16 text-genie-text-secondary">
              <p className="font-medium text-genie-text-primary mb-1">No vaults yet</p>
              <p className="text-sm">Create your first pronunciation vault to share with users.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Accent</TableHead>
                    <TableHead>Words</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vaults.map((vault) => (
                    <TableRow key={vault.id}>
                      <TableCell>
                        <div className="font-medium text-sm text-genie-text-primary">{vault.name}</div>
                        {vault.description && (
                          <div className="text-xs text-genie-text-secondary mt-0.5 line-clamp-1">{vault.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {vault.accent ? (
                          <Badge className="bg-genie-primary/10 text-genie-primary border-genie-primary/20">{vault.accent}</Badge>
                        ) : (
                          <span className="text-xs text-genie-text-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{vault.pairs.length}</TableCell>
                      <TableCell>
                        <Badge className={vault.is_public ? "bg-green-100 text-green-700 border-green-200" : "bg-genie-background-muted text-genie-text-secondary border-genie-border"}>
                          {vault.is_public ? "Public" : "Private"}
                        </Badge>
                        {vault.is_default && (
                          <Badge className="ml-1.5 bg-genie-primary/10 text-genie-primary border-genie-primary/20">Default</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(vault)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(vault)}
                            disabled={deleteVault.isPending}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <VaultDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        editingVault={editingVault}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <ApplyDefaultsSheet isOpen={applySheetOpen} onClose={() => setApplySheetOpen(false)} />

      <UpdateGeniesSheet changes={pronunciationChanges} onClose={() => setPronunciationChanges([])} />
    </div>
  );
};

export default PronunciationVaultsPage;
